import { PhArrowUp, PhArrowDown } from '@phosphor-icons/vue'

export const EMAIL_RE = /^[a-zA-Z0-9_+-]+(?:\.[a-zA-Z0-9_+-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/

export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 128
export const PASSWORD_UPPERCASE_RE = /[A-Z]/
export const PASSWORD_LOWERCASE_RE = /[a-z]/
export const PASSWORD_DIGIT_RE = /[0-9]/

export const COLLECTION_NAME_MAX_LENGTH = 50
export const COLLECTION_NAME_MIN_LENGTH = 3
export const COLLECTION_NAME_DISPLAY_MAX_LENGTH = 35

export const ITEM_LAYOUT_STYLE = {
  cards: 'cards',
  tiles: 'tiles',
  list: 'list',
  headlines: 'headlines',
  tierlist: 'tierlist',
}

// A display layout restyles the collection's one pinned and sorted list; an
// interactive layout owns its ordering, settings and editing rules and replaces
// that list entirely (CONTEXT.md). Frontend-only fact table — the backend keeps
// one flat layout enum (ADR-0001).
export const LAYOUT_KINDS = {
  display: 'display',
  interactive: 'interactive',
}

export const LAYOUT_KIND = {
  [ITEM_LAYOUT_STYLE.cards]: LAYOUT_KINDS.display,
  [ITEM_LAYOUT_STYLE.tiles]: LAYOUT_KINDS.display,
  [ITEM_LAYOUT_STYLE.list]: LAYOUT_KINDS.display,
  [ITEM_LAYOUT_STYLE.headlines]: LAYOUT_KINDS.display,
  [ITEM_LAYOUT_STYLE.tierlist]: LAYOUT_KINDS.interactive,
}

// Min grid column width (px) for the styles that render as a grid — shared by
// the browse item list and the layout-info preview. The other styles stack rows.
export const GRID_MIN_ITEM_WIDTH = {
  [ITEM_LAYOUT_STYLE.cards]: 300,
  [ITEM_LAYOUT_STYLE.tiles]: 240,
}

export const ALL_ITEM_LAYOUT_STYLES = Object.values(ITEM_LAYOUT_STYLE)

// The layouts that restyle the item list. Stands in wherever a layout set is
// missing or junk (old cached payloads, a new collection's defaults), so an
// interactive layout only ever shows on a collection that enabled it.
export const DISPLAY_ITEM_LAYOUT_STYLES =
  ALL_ITEM_LAYOUT_STYLES.filter(style => LAYOUT_KIND[style] === LAYOUT_KINDS.display)

// ==================== TIER LIST ====================
// Mirrors backend `libs/constants`.

export const MIN_TIER_LIST_TIERS = 1
export const MAX_TIER_LIST_TIERS = 100
// Most placements one Version may hold.
export const MAX_TIER_LIST_ITEMS = 400
export const TIER_NAME_MAX_LENGTH = 50

// Tiers of a collection that never saved its layout settings. The ids are
// generated once and permanent (ADR-0004) — a placement stores only a tierId,
// so these literals must stay byte-for-byte identical to the backend's.
export const DEFAULT_TIER_LIST_TIERS = [
  { _id: '6aa75a5a3273a19d57dc61ac', name: 'S', color: '#ff8c7d' },
  { _id: '6aa75a5a3273a19d57dc61ad', name: 'A', color: '#ffc17a' },
  { _id: '6aa75a5a3273a19d57dc61ae', name: 'B', color: '#ffdc7a' },
  { _id: '6aa75a5a3273a19d57dc61af', name: 'C', color: '#fff777' },
  { _id: '6aa75a5a3273a19d57dc61b0', name: 'D', color: '#bbf17a' },
  { _id: '6aa75a5a3273a19d57dc61b1', name: 'F', color: '#76ec7c' },
]

// The Tier colours Layout settings offers before the custom picker, the default
// Tiers' first; a new Tier takes the next one. Frontend only — the api checks the hex.
export const TIER_LIST_COLOR_PALETTE = [
  ...DEFAULT_TIER_LIST_TIERS.map(tier => tier.color),
  '#7dd3fc',
  '#a5b4fc',
  '#f0abfc',
  '#d4d4d8',
]

// How a tier list card renders (Layout settings, Items section).
export const TIER_LIST_ITEM_DISPLAY = {
  imageTitle: 'image_title',
  title: 'title',
  image: 'image',
}

// Layout settings label of each item display.
export const TIER_LIST_ITEM_DISPLAY_LABELS = {
  [TIER_LIST_ITEM_DISPLAY.imageTitle]: 'page.tier_list_settings.items.display.image_title',
  [TIER_LIST_ITEM_DISPLAY.title]: 'page.tier_list_settings.items.display.title',
  [TIER_LIST_ITEM_DISPLAY.image]: 'page.tier_list_settings.items.display.image',
}

// The Items section of a collection that never saved it (backend defaults).
export const DEFAULT_TIER_LIST_ITEM_SETTINGS = {
  display: TIER_LIST_ITEM_DISPLAY.imageTitle,
  keepImageRatio: false,
}

// The Participation section of a collection that never saved it (backend
// DEFAULT_TIER_LIST_PARTICIPATION): contributors and the community may both
// hold their own version.
export const DEFAULT_TIER_LIST_PARTICIPATION = {
  contributors: true,
  community: true,
}

// A tier list card's visual slot: 16:9 at a fixed width, so every card is
// equally tall. Keep original image ratio widens / narrows a file card at that
// height, within MIN..MAX (width / height), so a strip or panorama stays usable.
export const TIER_CARD_WIDTH = 120
export const TIER_CARD_ASPECT_RATIO = 16 / 9
export const TIER_CARD_MIN_RATIO = 2 / 3
export const TIER_CARD_MAX_RATIO = 3
// Lines of the centred title in a title only card.
export const TIER_CARD_TITLE_LINES = 3

// A Tier's name is a phrase of up to TIER_NAME_MAX_LENGTH characters, and it
// never makes its row taller (ADR-0006): the longer the name, the smaller it is
// set. Trimmed length up to and including a threshold takes that size; the
// default letters keep today's size. The classes are TierRow's own, since the
// app's type scale stops at the largest of the three.
export const TIER_NAME_LENGTH_THRESHOLD = {
  large: 8,
  medium: 24,
}
export const TIER_NAME_TEXT_CLASS = {
  large: 'tier-row__name--large',
  medium: 'tier-row__name--medium',
  small: 'tier-row__name--small',
}

// ==================== PRESENTATION MODE ====================
// The tier list alone on a stage, ready to be recorded (ADR-0006). Two routes of
// the app in the same tab, so the stage is a CSS box, not a browser window.

// The axis a stage ratio leaves room for the camera placeholder on: a column
// beside the board on a wide stage, a band above or below it on a tall one. A
// ratio with neither has no room for a placeholder at all.
export const STAGE_CAMERA_AXIS = {
  horizontal: 'horizontal',
  vertical: 'vertical',
}

// The four shapes a stage can take, in the order the launch page offers them
// under "Window size". Each is a key (what is stored), the ratio's two numbers
// (the box's `aspect-ratio`, and "16:9" wherever the ratio is written), the
// label beside them and the camera axis it offers. The first row is the
// first-time choice.
export const STAGE_RATIOS = [
  { key: 'widescreen', width: 16, height: 9, label: 'page.tier_list_presentation.ratio.widescreen', cameraAxis: STAGE_CAMERA_AXIS.horizontal },
  { key: 'vertical', width: 9, height: 16, label: 'page.tier_list_presentation.ratio.vertical', cameraAxis: STAGE_CAMERA_AXIS.vertical },
  { key: 'square', width: 1, height: 1, label: 'page.tier_list_presentation.ratio.square', cameraAxis: null },
  { key: 'portrait', width: 4, height: 5, label: 'page.tier_list_presentation.ratio.portrait', cameraAxis: null },
]

// Where the camera placeholder ends up on the stage, and what the launch page
// calls it. One name per axis and end, so the same stored position reads Left on
// a wide stage and Top on a tall one.
export const STAGE_CAMERA_PLACEMENT = {
  left: 'left',
  right: 'right',
  top: 'top',
  bottom: 'bottom',
}

// The three camera positions, in the order the launch page offers them under
// "Camera position placeholder". `start` and `end` are the two ends of whatever
// axis the ratio offers, so one stored position survives a change of ratio — and
// a ratio without an axis ignores it without erasing it. The first row is the
// first-time choice.
export const STAGE_CAMERA_POSITIONS = [
  { key: 'none', [STAGE_CAMERA_AXIS.horizontal]: null, [STAGE_CAMERA_AXIS.vertical]: null },
  { key: 'start', [STAGE_CAMERA_AXIS.horizontal]: STAGE_CAMERA_PLACEMENT.left, [STAGE_CAMERA_AXIS.vertical]: STAGE_CAMERA_PLACEMENT.top },
  { key: 'end', [STAGE_CAMERA_AXIS.horizontal]: STAGE_CAMERA_PLACEMENT.right, [STAGE_CAMERA_AXIS.vertical]: STAGE_CAMERA_PLACEMENT.bottom },
]

// The camera options name themselves after the placement they lead to, so the
// labels say where the placeholder will really be: "None", "Left", "Top"…
export const STAGE_CAMERA_LABEL_PREFIX = 'page.tier_list_presentation.camera.'

// The placeholder itself, from the mocks: a dark that is not the backdrop's
// black, so the stage's edge stays visible where the two touch, and the same in
// both themes because it stands for the recorded camera picture, not for app
// chrome.
export const STAGE_CAMERA_COLOR = '#202020'

// Where the launch choice lives: one per device, the same for every collection
// and never in the URL, so a reload of the stage keeps it.
export const STAGE_CHOICE_KEY = 'presentationStageChoice'

// The bars around the stage, and the system bar above it. Black in both themes
// (the stage itself follows the app theme), so it is never mistaken for the
// part to record.
export const STAGE_BACKDROP_COLOR = '#000000'

// Where the stage header takes the board's version switcher: the board teleports
// it here in presentation mode, so the header owns the layout and the switcher
// keeps its single home (ADR-0005).
export const STAGE_VERSION_MENU_ID = 'presentation-version-menu'

// What a placement points at. Values are the backend model names, and the half
// of a card's `resourceType:resourceId` key.
export const TIER_LIST_RESOURCE_TYPE = {
  collectionItem: 'CollectionItem',
  collection: 'Collection',
}

// The drag library's drop callbacks on one Tier row or the Queue (`dropMove`):
// a reorder inside it, a card arriving from another one, a card leaving it.
export const TIER_LIST_DROP = {
  update: 'update',
  add: 'add',
  remove: 'remove',
}

// Every Tier row and the Queue of a board share it, so cards drag between them.
export const TIER_LIST_DND_GROUP = 'tier-list'

// Which Version a Move writes: the collection's canonical version or the
// viewer's own.
export const TIER_LIST_TARGET = {
  own: 'own',
  canonical: 'canonical',
}

// The Board read's `?version=` for the collection's own version; a holder's is their userId.
export const TIER_LIST_CANONICAL_VERSION = 'canonical'

// The query key + value a gated "Create your own version" leaves on the collection
// URL, so the login / signup / verify round trip returns to the own version (ticket 14 §4).
export const TIER_LIST_VERSION_QUERY = 'version'
export const TIER_LIST_VERSION_QUERY_OWN = 'own'

// Holders per versions list page (backend `limit` enum 5 | 10 | 24 | 25); page 1
// adds the canonical row on top.
export const TIER_LIST_VERSIONS_PAGE_SIZE = 10

// The rows of the version switcher (buildVersionRows): the collection's version,
// the viewer's own ("You") and another holder's.
export const TIER_LIST_VERSION_ROW = {
  canonical: 'canonical',
  own: 'own',
  holder: 'holder',
}

// Which empty state a board shows (resolveBoardEmptyState); no state is `null`.
export const TIER_LIST_EMPTY_STATE = {
  // filters active, nothing matches → CollectionBookmarksNoResults `filters-active`
  noResults: 'no_results',
  // no filters, zero cards, Queue visible → CollectionBookmarksNoResults under the Tiers
  noCards: 'no_cards',
  // cards placed, Queue empty → the Queue's own message
  queueEmpty: 'queue_empty',
}

// Error codes a Move or a Layout settings save is answered with (backend
// `libs/errors`); `NOT_FOUND` also means the collection switched the tier list
// layout off, `INSUFFICIENT_PRIVILEGE` that the viewer may not save Layout settings.
export const TIER_LIST_ERROR = {
  insufficientPrivilege: 'INSUFFICIENT_PRIVILEGE',
  tierNotFound: 'TIER_LIST_TIER_NOT_FOUND',
  versionNotEditable: 'TIER_LIST_VERSION_NOT_EDITABLE',
  conflict: 'TIER_LIST_CONFLICT',
  itemLimitExceeded: 'TIER_LIST_ITEM_LIMIT_EXCEEDED',
  notFound: 'NOT_FOUND',
  rateLimitExceeded: 'RATE_LIMIT_EXCEEDED',
  // own-version writes of a community-only Participant (resolveOwnWriteGate)
  fullAccountRequired: 'FULL_ACCOUNT_REQUIRED',
  emailUnverified: 'EMAIL_UNVERIFIED',
  // a write without a valid session: a guest, or a session that expired (403)
  invalidToken: 'INVALID_TOKEN',
}

// Intro items ("item in description"): item copies shown beside the collection
// description. Cap mirrors the backend MAX_INTRO_ITEMS constant.
export const MAX_INTRO_ITEMS = 2

// Fixed compact intro-card width (px) on desktop — two thirds of a cards-style
// grid card; half (150px) was too narrow for "domain · date" under the headline.
// Deliberately independent of the per-collection layouts axis (grid-only).
export const INTRO_ITEM_CARD_WIDTH = Math.round((GRID_MIN_ITEM_WIDTH[ITEM_LAYOUT_STYLE.cards] * 2) / 3)

export const BOOKMARK_SORT_FIELDS = {
  createdAt: 'createdAt',
  title: 'title',
  domain: 'sld',
  sld: 'sld', // fix me (collection sorting)
  commentsNumber: 'commentsNumber',
  commentsDate: 'commentsDate',
  likes: 'likes',
}

export const BOOKMARK_SORT_OPTIONS = [
  {
    field: BOOKMARK_SORT_FIELDS.createdAt,
    asc: false,
  },
  {
    field: BOOKMARK_SORT_FIELDS.createdAt,
    asc: true,
  },
  {
    field: BOOKMARK_SORT_FIELDS.title,
    asc: true,
  },
  {
    field: BOOKMARK_SORT_FIELDS.title,
    asc: false,
  },
  {
    field: BOOKMARK_SORT_FIELDS.domain,
    asc: true,
  },
  {
    field: BOOKMARK_SORT_FIELDS.domain,
    asc: false,
  },
  {
    field: BOOKMARK_SORT_FIELDS.commentsNumber,
    asc: false,
  },
  {
    field: BOOKMARK_SORT_FIELDS.commentsNumber,
    asc: true,
  },
  {
    field: BOOKMARK_SORT_FIELDS.commentsDate,
    asc: false,
  },
  {
    field: BOOKMARK_SORT_FIELDS.commentsDate,
    asc: true,
  },
  {
    field: BOOKMARK_SORT_FIELDS.likes,
    asc: false,
  },
  {
    field: BOOKMARK_SORT_FIELDS.likes,
    asc: true,
  },
]

export const BOOKMARK_SORT_OPTIONS_UI = {
  [`${BOOKMARK_SORT_FIELDS.createdAt}-desc`]: {
    string: 'component.bookmarks.sorting.field.createdAt.desc',
    icon: PhArrowDown,
  },
  [`${BOOKMARK_SORT_FIELDS.createdAt}-asc`]: {
    string: 'component.bookmarks.sorting.field.createdAt.asc',
    icon: PhArrowUp,
  },

  [`${BOOKMARK_SORT_FIELDS.title}-asc`]: {
    string: 'component.bookmarks.sorting.field.title.asc',
    icon: PhArrowDown,
  },
  [`${BOOKMARK_SORT_FIELDS.title}-desc`]: {
    string: 'component.bookmarks.sorting.field.title.desc',
    icon: PhArrowUp,
  },

  [`${BOOKMARK_SORT_FIELDS.domain}-desc`]: {
    string: 'component.bookmarks.sorting.field.domain.desc',
    icon: PhArrowUp,
  },
  [`${BOOKMARK_SORT_FIELDS.domain}-asc`]: {
    string: 'component.bookmarks.sorting.field.domain.asc',
    icon: PhArrowDown,
  },

  [`${BOOKMARK_SORT_FIELDS.commentsNumber}-desc`]: {
    string: 'component.bookmarks.sorting.field.commentsNumber.desc',
    icon: PhArrowDown,
  },
  [`${BOOKMARK_SORT_FIELDS.commentsNumber}-asc`]: {
    string: 'component.bookmarks.sorting.field.commentsNumber.asc',
    icon: PhArrowUp,
  },

  // String key swap for comment date is intentional
  [`${BOOKMARK_SORT_FIELDS.commentsDate}-desc`]: {
    string: 'component.bookmarks.sorting.field.commentsDate.asc',
    icon: PhArrowDown,
  },
  [`${BOOKMARK_SORT_FIELDS.commentsDate}-asc`]: {
    string: 'component.bookmarks.sorting.field.commentsDate.desc',
    icon: PhArrowUp,
  },

  [`${BOOKMARK_SORT_FIELDS.likes}-asc`]: {
    string: 'component.bookmarks.sorting.field.likes.asc',
    icon: PhArrowUp,
  },
  [`${BOOKMARK_SORT_FIELDS.likes}-desc`]: {
    string: 'component.bookmarks.sorting.field.likes.desc',
    icon: PhArrowDown,
  },
}

export const COLLECTION_SORT_FIELDS = {
  createdAt: 'createdAt',
  name: 'name',
  newestItem: 'newestItem',
  oldestItem: 'oldestItem',
  title: 'title', // for items view sorting
}

export const COLLECTION_SORT_OPTIONS = [
  {
    field: COLLECTION_SORT_FIELDS.createdAt,
    asc: false,
  },
  {
    field: COLLECTION_SORT_FIELDS.createdAt,
    asc: true,
  },
  {
    field: COLLECTION_SORT_FIELDS.name,
    asc: true,
  },
  {
    field: COLLECTION_SORT_FIELDS.name,
    asc: false,
  },
  {
    field: COLLECTION_SORT_FIELDS.newestItem,
    asc: true,
  },
  {
    field: COLLECTION_SORT_FIELDS.oldestItem,
    asc: true,
  },
]

// Subset for Add to Collections picker (Title A-Z, Title Z-A, Newest, Oldest)
export const ADD_TO_COLLECTION_SORT_OPTIONS = [
  {
    field: COLLECTION_SORT_FIELDS.name,
    asc: true,
  },
  {
    field: COLLECTION_SORT_FIELDS.name,
    asc: false,
  },
  {
    field: COLLECTION_SORT_FIELDS.createdAt,
    asc: false,
  },
  {
    field: COLLECTION_SORT_FIELDS.createdAt,
    asc: true,
  },
]

export const COLLECTION_SORT_OPTIONS_UI = {
  [`${COLLECTION_SORT_FIELDS.createdAt}-desc`]: {
    string: 'component.collections.sorting.field.createdAt.desc',
    icon: PhArrowDown,
  },
  [`${COLLECTION_SORT_FIELDS.createdAt}-asc`]: {
    string: 'component.collections.sorting.field.createdAt.asc',
    icon: PhArrowUp,
  },

  [`${COLLECTION_SORT_FIELDS.name}-asc`]: {
    string: 'component.collections.sorting.field.title.asc',
    icon: PhArrowDown,
  },
  [`${COLLECTION_SORT_FIELDS.name}-desc`]: {
    string: 'component.collections.sorting.field.title.desc',
    icon: PhArrowUp,
  },

  [`${COLLECTION_SORT_FIELDS.newestItem}-asc`]: {
    string: 'component.collections.sorting.field.itemDate.desc',
    icon: PhArrowDown,
  },
  [`${COLLECTION_SORT_FIELDS.oldestItem}-asc`]: {
    string: 'component.collections.sorting.field.itemDate.asc',
    icon: PhArrowUp,
  },
}

export const MAX_READ_TIME = 60

// Post text: author-written intro shown above the item on its detail page.
// Rich text (bold/italic/link + native emoji). Max length is the hard cap; a
// "More"/"Less" toggle appears once the plain-text length passes the threshold.
export const POST_TEXT_MAX_LENGTH = 1000
export const POST_TEXT_MORE_THRESHOLD = 500
export const POST_TEXT_EDITOR_FEATURES = ['bold', 'italic', 'link']

// Signup route that upgrades an anonymous guest account to a full one
export const GUEST_SIGNUP_ROUTE = '/auth/signup?guest=true&autoLogin=false'

// Comments: shared text limit (mirrors backend COMMENT_TEXT_MAX_LENGTH) and page
// sizes for threaded lists (must be valid backend limit enum values: 5, 10, 25)
export const COMMENT_TEXT_MAX_LENGTH = 1000
export const COMMENTS_PAGE_SIZE = 5
export const REPLIES_PAGE_SIZE = 5
export const COMMENT_LIKES_PAGE_SIZE = 10

// Tag filter match operator: 'any' = match ANY selected tag (OR), 'all' = match ALL (AND)
export const TAG_MATCH = {
  ANY: 'any',
  ALL: 'all',
}

export const DEFAULT_TAG_MATCH = TAG_MATCH.ANY

// Collection Item Resource Types
export const ResourceType = {
  BOOKMARK: 'bookmark',
  NOTE: 'note',
  FILE: 'file', // user-uploaded file (images today, see FILE_UPLOAD)
}

// Add-item routes per resource type (used by the bookmark<->note type toggle and share intents)
export const ADD_ROUTES = {
  [ResourceType.BOOKMARK]: '/bookmarks/add',
  [ResourceType.NOTE]: '/notes/add',
  [ResourceType.FILE]: '/files/add',
}

// Query params on the add routes
export const ADD_QUERY = {
  ALLOW_TYPE_CHANGE: 'allowTypeChange', // boolean: show the bookmark<->note toggle
  PREFILL_CONTENT: 'prefillContent', // string: seed note content (e.g. URL carried bookmark->note)
}

// Note Colors
export const NoteColor = {
  YELLOW: 'yellow',
  GREEN: 'green',
  TURQUOISE: 'turquoise',
  PURPLE: 'purple',
  PINK: 'pink',
  RED: 'red',
  ORANGE: 'orange',
  GREY: 'grey',
  BODY: 'body',
}

export const bookmarkFilterDefaults = () => ({
  search: '',
  tags: [],
  isFavorite: false,
  sorting: [BOOKMARK_SORT_FIELDS.createdAt, false],
  readTime: [0, MAX_READ_TIME],
  languages: [],
})

export const collectionBookmarkFilterDefaults = () => ({
  search: '',
  tags: [],
  sorting: [BOOKMARK_SORT_FIELDS.createdAt, false],
  readTime: [0, MAX_READ_TIME],
  languages: [],
})

// Collection Items filter defaults (for v2 API - supports bookmarks and notes)
export const collectionItemFilterDefaults = () => ({
  search: '',
  tags: [],
  tagMatch: DEFAULT_TAG_MATCH,
  resourceType: null, // null = all types, or ResourceType.BOOKMARK / ResourceType.NOTE
  isFavorite: false,
  sorting: [BOOKMARK_SORT_FIELDS.createdAt, false],
  readTime: [0, MAX_READ_TIME],
  languages: [],
})

export const collectionItemsFilterDefaults = () => ({
  search: '',
  tags: [],
  tagMatch: DEFAULT_TAG_MATCH,
  resourceType: null,
  sorting: [BOOKMARK_SORT_FIELDS.createdAt, false],
  readTime: [0, MAX_READ_TIME],
  languages: [],
  addedBy: [],
})

export const collectionsFilterDefaults = () => ({
  search: '',
  sorting: [COLLECTION_SORT_FIELDS.createdAt, false],
})

export const STALE_TIME = 300e3

export const INVITE_LINK_RE = /^\/collections\/([a-f\d]+)\/invite-(link|email)\/([a-z0-9-]+)/i

export const THEME_NAMES = {
  auto: 'page.account.app_settings.theme.auto',
  light: 'page.account.app_settings.theme.light',
  dark: 'page.account.app_settings.theme.dark',
}

export const STYLE_NAMES = {
  [ITEM_LAYOUT_STYLE.cards]: 'page.account.app.layout.cards.title',
  [ITEM_LAYOUT_STYLE.list]: 'page.account.app.layout.lists.title',
  [ITEM_LAYOUT_STYLE.headlines]: 'page.account.app.layout.headlines.title',
  [ITEM_LAYOUT_STYLE.tiles]: 'page.account.app.layout.tiles.title',
  // Per-collection only, so it has no app-settings row of its own to name it.
  [ITEM_LAYOUT_STYLE.tierlist]: 'form.liststyle.tierlist',
}

export const STYLE_FORM_NAMES = {
  [ITEM_LAYOUT_STYLE.cards]: 'form.liststyle.cards',
  [ITEM_LAYOUT_STYLE.tiles]: 'form.liststyle.tiles',
  [ITEM_LAYOUT_STYLE.list]: 'form.liststyle.list',
  [ITEM_LAYOUT_STYLE.headlines]: 'form.liststyle.headlines',
  [ITEM_LAYOUT_STYLE.tierlist]: 'form.liststyle.tierlist',
}

export const SUB_COLLECTION_POSITION = {
  first: 'first',
  last: 'last',
}

export const SUB_COLLECTION_POSITION_NAMES = {
  [SUB_COLLECTION_POSITION.first]: 'form.field.collection_sub_collection_position.first',
  [SUB_COLLECTION_POSITION.last]: 'form.field.collection_sub_collection_position.last',
}

// Resources that render a "N items" / "3 out of 40 items" line under a page
// title, and the i18n keys backing each. Consumed by PageCountSubtitle.
export const COUNT_SUBTITLE_RESOURCE = {
  items: 'items',
  collections: 'collections',
}

export const COUNT_SUBTITLE_KEYS = {
  [COUNT_SUBTITLE_RESOURCE.items]: {
    total: 'common.items_n',
    filtered: 'common.items_n_filtered',
  },
  [COUNT_SUBTITLE_RESOURCE.collections]: {
    total: 'common.collections_n',
    filtered: 'common.collections_n_filtered',
  },
}

export const MAX_IMPORT_LIMIT = 1000

export const AddToCollectionMethod = {
  MOVE: 'move',
  COPY: 'copy',
}

export const HOW_TO_UPDATE_APP_LINKS = {
  android: {
    en: 'https://rubra.com/help/updating-rubra/android-native-app/',
    de: 'https://rubra.com/de/aktualisierung-von-rubra/aktualisieren-der-nativen-android-app/',
  },
  ios: {
    en: 'https://rubra.com/help/updating-rubra/ios-native-app/',
    de: 'https://rubra.com/de/aktualisierung-von-rubra/aktualisieren-der-nativen-ios-app/',
  },
  firefox: {
    en: 'https://rubra.com/help/updating-rubra/firefox-browser-extension/',
    de: 'https://rubra.com/de/aktualisierung-von-rubra/aktualisierung-der-firefox-browsererweiterung/',
  },
  safari: {
    en: 'https://rubra.com/help/updating-rubra/safari-browser-extension/',
    de: 'https://rubra.com/de/aktualisierung-von-rubra/aktualisieren-der-safari-browsererweiterung/',

  },
  edge: {
    en: 'https://rubra.com/help/updating-rubra/edge-browser-extension/',
    de: 'https://rubra.com/de/aktualisierung-von-rubra/aktualisieren-der-edge-browsererweiterung/',
  },
  chrome: {
    en: 'https://rubra.com/help/updating-rubra/chrome-browser-extension/',
    de: 'https://rubra.com/de/aktualisierung-von-rubra/aktualisieren-der-chrome-browsererweiterung/',
  },
  // Web-App (Should never show as it is updated automatically)
  web: {
    en: 'rubra.com/help/updating-rubra/web-app/',
    de: 'rubra.com/de/aktualisierung-von-rubra/aktualisieren-der-web-app/',
  },
}

export const UPDATE_APP_LINKS = {
  android: 'https://play.google.com/store/apps/details?id=com.rubra.appclient',
  ios: 'https://apps.apple.com/app/rubra-bookmark-manager/id1673761012',
  firefox: 'about:addons',
  safari: 'safari-extension://',
  edge: 'edge://extensions/',
  chrome: 'chrome://extensions/',
}

export const UPDATE_NEWS_LINKS = {
  de: 'https://rubra.com/de/update/',
  en: 'https://rubra.com/update/',
}

export const HELP_LINKS = {
  addBookmark: {
    en: 'https://rubra.com/help/using-rubra/add-items/adding-bookmarks/',
    de: 'https://rubra.com/de/eintrage-hinzufugen/hinzufuegen-von-lesezeichen/',
  },
}

// Custom collections branding (header banner + background). The header image is
// cropped on upload to exactly the size the band renders at, so a background
// image of the same height lines up with it pixel for pixel -- that is what
// lets a line drawn across the header continue into the background.
const BRANDING_HEADER_OUTPUT_SIZE = { width: 1332, height: 220 }

// Only PNG/JPG accepted; the picked file's own type is kept through the crop so
// PNG transparency survives (see ImageCropForm's output-type).
export const BRANDING = {
  HEADER_ASPECT_RATIO: BRANDING_HEADER_OUTPUT_SIZE.width / BRANDING_HEADER_OUTPUT_SIZE.height,
  HEADER_OUTPUT_SIZE: BRANDING_HEADER_OUTPUT_SIZE,
  HEADER_MAX_HEIGHT_PX: BRANDING_HEADER_OUTPUT_SIZE.height,
  ALLOWED_IMAGE_MIME: ['image/jpeg', 'image/png'],
  IMAGE_SIZE_LIMIT: 10e6, // 10MB, matches backend config.server.uploads.branding
  DEFAULT_BACKGROUND_COLOR: '#7a1f3d',
}

// User file uploads ("Upload image"). Mirrors backend config.fileUploads.image — the server
// decides from the bytes; this list only drives the picker and the client-side pre-check.
export const FILE_UPLOAD = {
  ACCEPT_MIME: [
    'image/jpeg',
    'image/jpg', // non-standard, sent by some Android apps
    'image/png',
    'image/apng',
    'image/webp',
    'image/gif',
    'image/avif',
    'image/svg+xml',
  ],
  ACCEPT_EXT: ['jpg', 'jpeg', 'png', 'apng', 'webp', 'gif', 'avif', 'svg'],
  SIZE_LIMIT: 10e6, // bytes, matches backend config.fileUploads.image.fileSizeLimit
  SCAN_POLL_MS: 3000, // scan-phase status poll (only when the api answers scanStatus 'pending')
  SCAN_POLL_MAX_MS: 300000, // give up after 5 min — the item still appears once the scan clears it
}

export const DEFAULT_DURATION = '30d'
export const DEACTIVATION_DURATION = '-1h'

export const MAX_COLLECTIONS_DEEPNESS = 1

// Query param key used by focus-layout pages (edit tags, manage members, etc.)
// to know where to return to after the user finishes the flow.
export const BACK_TARGET_QUERY = 'from'

// Query param the add flows (/bookmarks/add, /notes/add, /files/add) append to
// the BACK_TARGET_QUERY target after a save, carrying the new item's id — the
// originating page (e.g. the item picker) can pick the fresh item up.
export const CREATED_ITEM_QUERY = 'createdItemId'

// Desktop container width (px) for detail pages that show content and comments
// side by side (see DetailColumnsLayout). Wider than the default 720px focus
// width so both columns stay readable.
export const DETAIL_WITH_COMMENTS_MAX_WIDTH = 1080

export const CREATE_COLLECTION_ROUTE = '/collections/add'

// "Add details" screen shown right after an item is saved (ItemAddDetails.vue):
// staged collections + tags + post text. Reached as a route by the native share
// flow and wrapped by /extension/options in the browser extension.
export const ADD_DETAILS_ROUTE = '/items/:id/add-details'

export const ADD_DETAILS_QUERY = {
  COLLECTION_ID: 'collectionId', // collection the item currently lives in
  PICKER: 'picker', // the collection picker step, so back/forward walk the steps
}

// Value of ADD_DETAILS_QUERY.PICKER while the picker step is open.
export const PICKER_STEP_ON = '1'

// rubra:// scheme registered in AndroidManifest.xml. The Android share runs in a
// separate Activity (SendIntentActivity), so leaving the share flow has to launch
// MainActivity explicitly; App Links via the website URL are unreliable.
export const ANDROID_MAIN_ACTIVITY_URL = 'rubra:///bookmarks'
